import {
  Component,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  Input,
  AfterViewInit,
  OnInit,
} from "@angular/core";
import {
  BfxBlotterFilter,
  BfxBlotterFilterId,
  FilterType,
  ExtendedOrderTypeIdentifier,
} from "./bfx-blotter-filter.constants";
import { TableDefinition, SystemConfigPromiseService, Roles } from "shared/cubiq-services";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import { TranslateService } from "@ngx-translate/core";
import { AssetClass } from "shared/cubiq-model-multi-asset/model/enum/AssetClass";
import { FILTER_SELECTOR_MODES } from "./models/bfx-blotter-filter.constants";

import { LoggerService } from "@bfx/logger";
import { BfxBlotterFilterService } from "./bfx-blotter-filter.service";

const logger = LoggerService.for(__dirname);

@Component({
  selector: ".bfx-blotter-filter-list",
  templateUrl: "./html/bfx-blotter-filter-list.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BfxBlotterFilterListComponent implements AfterViewInit, OnInit {
  public _blotterTypeId: BfxBlotterFilterId;
  public _extendedTypeId: ExtendedOrderTypeIdentifier;

  public tableDefinition: TableDefinition;
  public filterType: FilterType;
  public blotterId: string;

  public selection: Array<BfxBlotterFilter> = [];
  public _items: Array<BfxBlotterFilter> = new Array<BfxBlotterFilter>();
  public myItems: Array<BfxBlotterFilter> = [];
  public filter: boolean = false;
  public edited: boolean = false;
  public emptyMessage: string;
  public favoriteFilterIds: any = {};

  public showNewDialog: boolean = false;
  public filterSelectionModes: Array<any> = FILTER_SELECTOR_MODES;

  protected selectedFilter: string;

  @Input()
  public multiSortMeta = [
    { field: "favorite", order: -1 },
    { field: "filterName", order: 1 },
  ];
  private adminUnrestricted: boolean = false;
  public hasUnrestrictedRights: boolean = false;
  public isInViewEditMode: boolean = false;

  @ViewChild("dt", { static: true }) protected filterList: ElementRef;

  constructor(
    public blotterService: BfxBlotterFilterService,
    public translationService: TranslateService,
    private systemConfigService: SystemConfigPromiseService,
    private _element: ElementRef,
  ) {
    this.systemConfigService.hasRole(Roles.ADMIN_FILTER_UNRESTRICTED).then((value) => {
      logger.out.info(`BfxBlotterFilterViewComponent: ADMIN_FILTER_UNRESTRICTED ${value}`);
      this.adminUnrestricted = value;
    });

    this.selectedFilter = this.blotterService._selFilter;
  }

  ngOnInit() {
    this.blotterService.registerFilterList(
      this,
      this.blotterTypeId.blotterType,
      this.blotterTypeId.assetClass,
      this.extendedTypeId,
    );
    this.blotterService.isEditableMode.subscribe(this.onViewEditEvent.bind(this));
  }

  onViewEditEvent(isInViewEditMode: boolean) {
    this.isInViewEditMode = isInViewEditMode;
    if (!isInViewEditMode) this.resetListTable();
  }

  getAppliedSort(): any {
    return this.filterList["multiSortMeta"];
  }

  ngAfterViewInit() {
    this.blotterService.modelUpdateSubject.subscribe(this.refreshItems.bind(this));
  }

  public initTableDefinition(
    tableDefinition: TableDefinition,
    filterType: FilterType,
    blotterId: string,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): void {
    this.blotterId = blotterId;
    this.tableDefinition = tableDefinition;
    this.filterType = filterType;
  }

  public refreshItems(): void {
    this.emptyMessage = "Loading ....";
    let callFn: any =
      this.selectedFilter === "myFilters"
        ? this.blotterService.getMyItems
        : this.blotterService.getItems;
    callFn = callFn.bind(this.blotterService);
    let promiseItems: Promise<Array<BfxBlotterFilter>> = callFn(
      this.filterType,
      this.blotterTypeId,
      this.extendedTypeId,
    );
    promiseItems.then((items: Array<BfxBlotterFilter>) => {
      if (this.selectedFilter === "myFilters") {
        this.myItems = items;
      } else {
        this.blotterService
          .getMyItems(this.filterType, this.blotterTypeId, this.extendedTypeId)
          .then((myResults: Array<BfxBlotterFilter>) => {
            this.myItems = myResults;
          });
      }
      this._items = items;
      this.blotterService
        .getFavoriteFilters(this.blotterTypeId, this.extendedTypeId)
        .then((ids: Array<string>) => {
          this._items.forEach((item) => {
            item.favorite = ids.indexOf(item.filterId) >= 0;
          });
          this.emptyMessage = this.translationService.instant("filter.manager.no.filters");
        });
      if (this.selection && this.selection.length > 0) {
        let selectedItem = this._items.find((item) => {
          return item.filterId == this.selection[0].filterId;
        });
        if (selectedItem != undefined) {
          this.resetListTable();
          this.selection.push(selectedItem);
        }
      }
      this.resort();
    });
  }
  public resort() {
    if (this.multiSortMeta && this.multiSortMeta.length > 0) {
      setTimeout(() => {
        this.filterList["sortMultiple"]();
      }, 0);
    }
  }

  public onRowSelect(event: any): void {
    if (this.isInViewEditMode) return;
    this.validateActions();
    this.blotterService.setEditMode(
      false,
      this.blotterTypeId.blotterType,
      this.blotterTypeId.assetClass,
      this.extendedTypeId,
    );
    this.editItem();
  }

  public get items(): Array<BfxBlotterFilter> {
    return this._items;
  }

  public get filterSelection(): string {
    return this.selectedFilter;
  }

  public set filterSelection(selection: string) {
    this.selectedFilter = selection;
    this.refreshItems();
    this.blotterService._selFilter = selection;
  }

  public confirmDeleteFilters(): void {
    let element: Element = this._element.nativeElement;
    this.blotterService.confirmDeleteFilter(
      this.deleteFilters.bind(this),
      element.ownerDocument.body,
    );
  }

  public createNewFilter(): void {
    this.resetListTable();
    let element: Element = this._element.nativeElement;
    this.blotterService.createNewFilter(
      this.onNewFilter.bind(this),
      true,
      this.filterType,
      this.blotterTypeId.blotterType,
      element.ownerDocument.body,
      this.extendedTypeId,
    );
  }

  public onNewFilter(newFilterName: string) {
    let blotterType: BlotterType = this.blotterTypeId.blotterType;
    let assetClass: AssetClass = this.blotterTypeId.assetClass;

    if (newFilterName && newFilterName.length > 0) {
      this.blotterService
        .createFilter(
          this.filterType,
          newFilterName,
          this._blotterTypeId.blotterType,
          this._blotterTypeId.assetClass,
          false,
          this.extendedTypeId,
        )
        .then(
          (result: any) => {},
          (errors: any[]) => this.blotterService.handleErrors(errors, blotterType, assetClass),
        );
    }

    this.blotterService.isEditableMode.next(true);
  }

  public deleteFilters() {
    let ids: string[] = this.selection.map((a) => a.filterId);
    let blotterType: BlotterType = this.blotterTypeId.blotterType;
    let assetClass: AssetClass = this.blotterTypeId.assetClass;

    this.blotterService.deleteFilters(ids, blotterType, assetClass, this.extendedTypeId).then(
      (result: any) => {},
      (errors: any[]) => this.blotterService.handleErrors(errors, blotterType, assetClass),
    );
  }

  public get blotterTypeId(): BfxBlotterFilterId {
    return this._blotterTypeId;
  }

  public get extendedTypeId(): ExtendedOrderTypeIdentifier {
    return this._extendedTypeId;
  }

  public editItem(): void {
    this.selection[0].filterIdentifier = this._blotterTypeId;
    if (this.selection.length > 1) {
      this.blotterService.clearView(
        this.blotterTypeId.blotterType,
        this.blotterTypeId.assetClass,
        this.extendedTypeId,
      );
      return;
    }
    this.blotterService.editItem(this.selection[0]);
  }

  public toggleFavorite(data: any): void {
    data.favorite = !data.favorite;
    this.resort();
    this.blotterService
      .getFavoriteFilters(this.blotterTypeId, this.extendedTypeId)
      .then((ids: Array<string>) => {
        let favoriteFilterIds: Array<string> = ids;
        let index: number = ids.indexOf(data.filterId);
        if (data.favorite == false && index >= 0) favoriteFilterIds.splice(index, 1);
        if (data.favorite == true && index == -1) favoriteFilterIds.push(data.filterId);
        this.blotterService.setFavoriteFilters(
          favoriteFilterIds,
          this.blotterTypeId,
          this.extendedTypeId,
        );
      });
  }

  @Input()
  public set blotterTypeId(blotterTypeId: BfxBlotterFilterId) {
    this._blotterTypeId = blotterTypeId;
    this.refreshItems();
  }

  @Input()
  public set extendedTypeId(extendedTypeId: ExtendedOrderTypeIdentifier) {
    this._extendedTypeId = extendedTypeId;
    this.refreshItems();
  }

  public editItemFromButton() {
    this.blotterService.setEditMode(
      true,
      this.blotterTypeId.blotterType,
      this.blotterTypeId.assetClass,
      this.extendedTypeId,
    );
    this.editItem();
    this.blotterService.isEditableMode.next(true);
  }

  public applySelectedFilters(): void {
    let event: any = {
      blotterId: this.blotterId,
      items: this.selection.map((item: BfxBlotterFilter) => item.filterId),
    };
    this.blotterService.applyFilterConfig(event);
  }

  private validateActions(): void {
    if (
      this.myItems.filter((c) => c.filterId == this.selection[0].filterId).length > 0 ||
      this.adminUnrestricted
    ) {
      this.hasUnrestrictedRights = true;
    } else {
      this.hasUnrestrictedRights = false;
    }
  }

  public copyFilter(event): void {
    let element: Element = this._element.nativeElement;
    this.blotterService.copyFilterService(
      event,
      this.selection[0],
      this.tableDefinition,
      this.filterType,
      element.ownerDocument.body,
    );
  }

  public resetListTable() {
    this.selection = [];
  }

  public applyFavoriteStyle(filter: any): string {
    return filter.favorite ? "favorite" : "nonFavorite";
  }

  onClickFilterSearch(filter: boolean, dt: any): boolean {
    dt.reset();
    return !filter;
  }
}
